import React from "react";
import { classNames } from "primereact/utils";
import { useFormContext, useWatch } from "react-hook-form";
import "./SmartFilterButton.scss";

export type SmartFilterButtonProps = {
    label: React.ReactNode;
    name?: string;
    active?: boolean;
    onClick?: () => void;
    disabled?: boolean;
    className?: string;
};

type SmartFilterButtonVisualProps = {
    label: React.ReactNode;
    active: boolean;
    onClick: () => void;
    disabled?: boolean;
    className?: string;
};

const SmartFilterButtonVisual: React.FC<SmartFilterButtonVisualProps> = ({
    label,
    active,
    onClick,
    disabled,
    className,
}) => (
    <button
        type="button"
        className={classNames(
            "smart-filter-button p-button p-component",
            active && "p-highlight",
            className,
        )}
        aria-pressed={active}
        disabled={disabled}
        onClick={onClick}
    >
        <span className="p-button-label">{label}</span>
    </button>
);

const SmartFilterButtonForm: React.FC<
    Omit<SmartFilterButtonProps, "active" | "onClick"> & { name: string }
> = ({ name, label, disabled, className }) => {
    const { control, setValue } = useFormContext();
    const value = useWatch({ control, name }) as boolean | undefined;
    const active = Boolean(value);

    return (
        <SmartFilterButtonVisual
            label={label}
            active={active}
            disabled={disabled}
            className={className}
            onClick={() =>
                setValue(name, !active, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: false,
                })
            }
        />
    );
};

export const SmartFilterButton: React.FC<SmartFilterButtonProps> = (props) => {
    const { name, label, active, onClick, disabled, className } = props;

    if (name) {
        return (
            <SmartFilterButtonForm
                name={name}
                label={label}
                disabled={disabled}
                className={className}
            />
        );
    }

    if (active === undefined || !onClick) {
        throw new Error("SmartFilterButton requires either name or active+onClick");
    }

    return (
        <SmartFilterButtonVisual
            label={label}
            active={active}
            onClick={onClick}
            disabled={disabled}
            className={className}
        />
    );
};
